import {getProtectedSessionStorage} from '@app/features/platform/state/ProtectedWebStorage';

// SPDX-License-Identifier: AGPL-3.0-or-later

const STORAGE_KEY = 'konvo.voice.disconnectHistory.v1';
const MAX_ENTRIES = 30;

interface ConnectionState {
	channelId: string | null;
	connectionId: string | null;
	connected: boolean;
	connecting: boolean;
	reconnecting: boolean;
}

export interface VoiceDisconnectEntry extends ConnectionState {
	timestamp: string;
	source: string;
	reason: string;
}

type HistoryStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function boundedString(value: unknown): string | null {
	return typeof value === 'string' ? value.slice(0, 128) : null;
}

// Only retain explicit connection metadata, never tokens, media or message content.
function parseEntry(value: unknown): VoiceDisconnectEntry | null {
	if (value === null || typeof value !== 'object') return null;
	const entry = value as Record<string, unknown>;
	if (typeof entry.timestamp !== 'string' || !Number.isFinite(Date.parse(entry.timestamp))) return null;
	if (typeof entry.source !== 'string' || typeof entry.reason !== 'string') return null;
	return {
		timestamp: new Date(entry.timestamp).toISOString(),
		source: entry.source.slice(0, 128),
		reason: entry.reason.slice(0, 128),
		channelId: boundedString(entry.channelId),
		connectionId: boundedString(entry.connectionId),
		connected: entry.connected === true,
		connecting: entry.connecting === true,
		reconnecting: entry.reconnecting === true,
	};
}

export class VoiceDisconnectHistory {
	private entries: Array<VoiceDisconnectEntry> | null = null;

	constructor(private readonly storage: () => HistoryStorage | null) {}

	getEntries(): Array<VoiceDisconnectEntry> {
		if (this.entries === null) {
			this.entries = [];
			try {
				const parsed: unknown = JSON.parse(this.storage()?.getItem(STORAGE_KEY) ?? '[]');
				if (Array.isArray(parsed)) {
					this.entries = parsed.slice(-MAX_ENTRIES).flatMap((value) => {
						const entry = parseEntry(value);
						return entry ? [entry] : [];
					});
				}
			} catch {
				// Diagnostics must never prevent a call from ending, including on unload.
			}
		}
		return this.entries.map((entry) => ({...entry}));
	}

	record(source: string, reason: string, state: ConnectionState): void {
		const entry = parseEntry({...state, timestamp: new Date().toISOString(), source, reason});
		if (!entry) return;
		this.entries = [...this.getEntries(), entry].slice(-MAX_ENTRIES);
		try {
			this.storage()?.setItem(STORAGE_KEY, JSON.stringify(this.entries));
		} catch {
			// Keep the in-memory history when storage is unavailable or full.
		}
	}

	clear(): void {
		this.entries = [];
		try {
			this.storage()?.removeItem(STORAGE_KEY);
		} catch {
			// Storage may be disabled by the browser.
		}
	}
}

// Session storage survives reloads, stays local to this window, and expires when
// the window closes. It is also explicitly cleared on account logout.
export const voiceDisconnectHistory = new VoiceDisconnectHistory(() => getProtectedSessionStorage());
